import { SignatureKeyNotProvidedError } from '../errors/signature-key-not-provided-error';
import { UserKeyPendingOrExpiredError } from '../errors/user-key-pending-or-expired-error';
import { areEqualUint8Array, base64ToArrayBuffer, base64ToByteArray, combineUint8Arrays, stringToArrayBuffer, stringToByteArray } from '../utils/utils';
export class KeyService {
    constructor(uiFrameAdapter, userKeysRepository, cryptoService, logger) {
        this.uiFrameAdapter = uiFrameAdapter;
        this.userKeysRepository = userKeysRepository;
        this.cryptoService = cryptoService;
        this.logger = logger;
    }
    /** Prompts for signature key only when needed, dictated by ask frequency. */
    async prompt(vault, modalText, previousAskFrequency, errorCode) {
        // try resolve raw key pair using the signature key entered
        const { password, askFrequency } = await this.promptForSignatureKey(modalText, errorCode, previousAskFrequency);
        const keyPair = password ? await this.findKeyPairBySignatureKey(vault, password) : null;
        return { keyPair, askFrequency };
    }
    async findKeyPairBySignatureKey(vault, signatureKey) {
        // load user's all keys
        const userKeys = await this.userKeysRepository.getPrivateKeysByUserId(this.uiFrameAdapter.companyId, this.uiFrameAdapter.userId);
        // compute salt
        const salt = combineUint8Arrays(stringToByteArray(`${this.uiFrameAdapter.companyId}`), stringToByteArray(`${this.uiFrameAdapter.userId}`));
        // try to find private-public key pair by signature key
        for (const key of userKeys) {
            const ivBytes = new Uint8Array(base64ToArrayBuffer(key.iv));
            const encryptedPrivateKeyBytes = base64ToArrayBuffer(key.encryptedPrivateKey);
            try {
                const privateKey = await this.cryptoService.decryptPrivateKey(stringToArrayBuffer(signatureKey), salt, encryptedPrivateKeyBytes, ivBytes);
                this.validateUserKeyByVault(vault, key);
                const publicKey = await this.cryptoService.importPublicKey(base64ToArrayBuffer(key.publicKey));
                return {
                    privateKey,
                    publicKey
                };
            }
            catch (error) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                if (error.name === 'OperationError') {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    this.logger.logWarning(error);
                    continue;
                }
                else if (error instanceof UserKeyPendingOrExpiredError) {
                    this.logger.logWarning(`Key with id '${key.id}' is not valid according to the current state of the Vault.`);
                    continue;
                }
                else {
                    throw error;
                }
            }
        }
        return null;
    }
    async promptForSignatureKey(modalText, errorCode, askFrequency) {
        const result = await this.uiFrameAdapter.triggerSignatureKeyModal(modalText, errorCode, askFrequency);
        if (result.password === null) {
            throw new SignatureKeyNotProvidedError();
        }
        return result;
    }
    validateUserKeyByVault(vault, userKey) {
        if (!vault.effectiveSignatureEntries.some(entry => areEqualUint8Array(entry.requestorPublicKey, base64ToByteArray(userKey.publicKey)))) {
            throw new UserKeyPendingOrExpiredError();
        }
    }
}
